import "./App.scss";
import { Outlet, useLocation } from "react-router-dom";
import { Suspense, useEffect, useRef, useState } from "react";
import { I18nextProvider } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import { i18n } from "./i18n.config";
import { useAppSelector } from "./store";
import { GrayListBanner } from "./components/GrayListBanner/GrayListBanner";
import { ImpersonationBanner } from "./components/ImpersonationBanner/ImpersonationBanner";
import { BROWSER_SUPPORT } from "./common/enums/browser-enums";
import { ComponentLoader } from "./components/ComponentLoader/ComponentLoader";
import { SetBootForCurrentBusiness } from "./reducers/model/boot";
import { useHandleCookie } from "./common/util/cookies";
import { FullPageComponentLoader } from "./common/components/FullPageComponentLoader/FullPageComponentLoader";
import { isAuthRoute, isUrlIncludesRoutes } from "./common/util/routeUtils";
import { useLocaleManagement } from "./common/hooks/useLocaleManagement";
import { useAppInitialization } from "./common/hooks/useAppInitialization";
import { useAuthentication } from "./common/hooks/useAuthentication";
import { useBrowserDetection } from "./common/hooks/useBrowserDetection";
import { usePeriodSelectorVisibility } from "./common/hooks/usePeriodSelectorVisibility";
import { useRoutingEffects } from "./common/hooks/useRoutingEffects";
import { useAnalytics } from "./common/hooks/useAnalytics";
import { SiteHeader } from "./components/App/SiteHeader/SiteHeader";
import { SiteFooter } from "./components/App/SiteFooter/SiteFooter";
import ABOProfile from "./components/ABOProfile/ABOProfile";
import DownlineProfileModal from "./components/DownlineProfile/PlatinumProfile/DownlineProfileModal";
import CustomerProfileModal from "./components/DownlineProfile/CustomerProfile/CustomerProfileModal";
import { ROUTE_PATHS } from "./common/enums/routing-enums";

const isComponentRoute = !isUrlIncludesRoutes();

const App = () => {
  const { isLocaleLoaded, currentLocale } = useLocaleManagement();

  const boot = useAppSelector(({ boot }) => boot);
  const { user } = boot;
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { maintenanceConfig } = useAppSelector((state) => state.boot.configuration);
  const { pathname } = useLocation();

  // Initialization hook (handles display mode and site tools)
  const { isNewAboLogin } = useAppInitialization({ boot, pathname });

  // Authentication hook (handles maintenance check and login verification)
  const { isAuthenticated } = useAuthentication({ user, pathname, selectedPeriod, maintenanceConfig });

  const { browserStatus } = useBrowserDetection();
  const displayPeriodSelector = usePeriodSelectorVisibility();
  useRoutingEffects();
  useAnalytics();

  // Other hooks
  const aboName = useAppSelector((state) => state.aboName);
  const { handleCookieLogic } = useHandleCookie(boot, currentLocale, aboName);
  const handleCookieLogicRef = useRef(handleCookieLogic);
  handleCookieLogicRef.current = handleCookieLogic;
  // Auth and error routes (isComponentRoute false) need no restored session, so don't flash the loader there.
  const [isCookieRecoveryPending, setIsCookieRecoveryPending] = useState(!user.access_token && isComponentRoute);

  useEffect(() => {
    let isMounted = true;

    if (!user.access_token) {
      handleCookieLogicRef
        .current()
        .catch(() => {
          // Allow the normal authentication flow to handle failed recovery.
        })
        .finally(() => {
          if (isMounted) setIsCookieRecoveryPending(false);
        });
    } else if (isMounted) {
      setIsCookieRecoveryPending(false);
    }

    return () => {
      isMounted = false;
    };
  }, [user.access_token]);

  useEffect(() => {
    // When boot state is populated, write the cookie so other tabs can use it.
    if (isNewAboLogin && pathname !== ROUTE_PATHS.LOG_OUT) {
      handleCookieLogic();
    }
  }, [handleCookieLogic, isNewAboLogin, pathname]);

  useEffect(() => {
    if (user.access_token && user.abo) {
      SetBootForCurrentBusiness({ user });
    }
    //eslint-disable-next-line
  }, []);

  const { isImpersonation, configuration } = useAppSelector((state) => state.boot);
  const { showImpersonationBanner } = configuration;
  const { sideNavExpanded, impersonationBannerCollapsed } = useAppSelector((state) => state.uiState);
  const { previousElement, useCurrentMonthData, showOnlyPerformanceHistory } = useAppSelector((state) => state.profile);
  const isAuthPage = isAuthRoute(pathname);
  const isErrorPage = pathname.startsWith("/error/");
  const showImpersonation = isImpersonation && showImpersonationBanner && !isAuthPage && !impersonationBannerCollapsed;

  if (isCookieRecoveryPending) {
    return <FullPageComponentLoader />;
  }

  return isLocaleLoaded ? (
    <div
      className={`App ${sideNavExpanded ? "App--side-nav-expanded" : ""} ${showImpersonation ? "App--impersonation-active" : ""} ${isAuthPage || isErrorPage ? "App--auth-route" : ""}`}
      id="App"
    >
      <a href="#App__component-section" className="App__skip-nav">
        Skip to main content
      </a>
      <ImpersonationBanner />
      <SiteHeader displayPeriodSelector={displayPeriodSelector} />
      <div id="App__component-section" className="App__component-section">
        <GrayListBanner show={browserStatus === BROWSER_SUPPORT.GRAYLIST} />
        <div className={`App__content-wrapper ${isAuthPage || isErrorPage ? "App__content-wrapper--centered" : ""}`}>
          <I18nextProvider i18n={i18n}>
            <Suspense fallback={isComponentRoute ? <FullPageComponentLoader /> : <></>}>
              <ComponentLoader
                isComponentRoute={isComponentRoute}
                isSignedIn={isAuthenticated}
                key={isComponentRoute ? user.abo : "outlet"}
                period={selectedPeriod}
                route={pathname}
              >
                <Outlet />
              </ComponentLoader>
            </Suspense>
          </I18nextProvider>
        </div>
      </div>
      <SiteFooter />
      <ABOProfile
        previousElement={previousElement}
        useCurrentMonthData={useCurrentMonthData}
        showOnlyPerformanceHistory={showOnlyPerformanceHistory}
      />
      <DownlineProfileModal />
      <CustomerProfileModal />
    </div>
  ) : (
    <></>
  );
};

export default App;
